import { useEffect } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  Skeleton,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  selectConversations,
  fetchConversationsAsync,
  selectLoadedConversations,
  selectLoadingConversations,
  fetchConversationMessages,
  setConversationMessages,
  setSelectedConversationID,
  selectSelectedConversationID,
} from '../../../data/ai-services.slice';
import { useTranslation } from 'react-i18next';
import { EditNote, Search } from '@mui/icons-material';
import { DateTime } from 'luxon';

const PREFIX = 'Conversations';

const componentClasses = {
  wrapper: `${PREFIX}-wrapper`,
  conversation: `${PREFIX}-conversation`,
  conversationTitle: `${PREFIX}-conversationTitle`,
  conversationMetadata: `${PREFIX}-conversationMetadata`,
  listSection: `${PREFIX}-listSection`,
  selectedConversation: `${PREFIX}-selectedConversation`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${componentClasses.wrapper}`]: {
    padding: theme.spacing(0),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    width: '210px',
    alignItems: 'flex-start',
    overflowX: 'hidden',
    overflowY: 'auto',
    whiteSpace: 'nowrap',
  },
  [`& .MuiButton-text`]: {
    padding: 0,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: '210px',
    justifyContent: 'flex-start',
  },
  [`& .${componentClasses.listSection}`]: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    alignItems: 'flex-start',
  },
  [`& .${componentClasses.conversationTitle}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    alignItems: 'flex-start',
    padding: theme.spacing(0.25, 0.5),

    [`&.${componentClasses.selectedConversation}, &:hover`]: {
      color: theme.palette.secondary.main,
      backgroundColor: theme.palette.action.disabled,
    },
  },
  [`& .${componentClasses.conversationMetadata}`]: {
    display: 'flex',
    flexDirection: 'row',
    fontSize: '10px',
    lineHeight: '10px',
    color: theme.palette.text.secondary,
    gap: theme.spacing(0.25),
    alignItems: 'center',
  },
}));

const StyledSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  alignItems: 'flex-start',
}));

const Conversations = () => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const conversations = useAppSelector(selectConversations);
  const selectedConversationID = useAppSelector(selectSelectedConversationID);
  const loadingConversations = useAppSelector(selectLoadingConversations);
  const loadedConversations = useAppSelector(selectLoadedConversations);

  const handleConversationClick = (conversationID: string) => {
    dispatch(fetchConversationMessages(conversationID));
  };

  const handleNewConversationClick = () => {
    dispatch(setConversationMessages([]));
    dispatch(setSelectedConversationID(null));
  };

  useEffect(() => {
    dispatch(fetchConversationsAsync());
  }, []);

  return (
    <StyledBox className={componentClasses.wrapper}>
      <StyledSection>
        <Button onClick={handleNewConversationClick} variant="text">
          <EditNote />
          {t('aiServices.newConversation')}
        </Button>
        <Button variant="text" disabled>
          <Search />
          {t('aiServices.searchConversations')}
        </Button>
      </StyledSection>
      {loadingConversations && !loadedConversations && (
        <>
          <Skeleton variant="rectangular" height={16} width={100} />
          <Skeleton variant="rectangular" height={16} width={80} />
          <Skeleton variant="rectangular" height={16} width={120} />
          <Skeleton variant="rectangular" height={16} width={90} />
        </>
      )}
      {loadedConversations && (
        <StyledSection className={componentClasses.listSection}>
          <Typography variant="body2" color="text.secondary">
            {t('aiServices.conversations')}
          </Typography>
          {[...conversations].reverse().map((conversation) => {
            const isSelected =
              selectedConversationID === conversation.conversationSessionID;
            const buttonClasses = [componentClasses.conversationTitle];
            if (isSelected) {
              buttonClasses.push(componentClasses.selectedConversation);
            }
            return (
              <Tooltip
                key={conversation.conversationSessionID}
                placement="right"
                arrow
                title={DateTime.fromISO(
                  conversation.modifiedOnUTC,
                ).toLocaleString(DateTime.DATETIME_MED)}>
                <Button
                  onClick={() =>
                    handleConversationClick(conversation.conversationSessionID)
                  }
                  className={buttonClasses.join(' ')}
                  variant="text">
                  {conversation.title}
                </Button>
              </Tooltip>
            );
          })}
        </StyledSection>
      )}
    </StyledBox>
  );
};

export { Conversations };
